import { Grid, createStyles, makeStyles } from '@material-ui/core';
import * as React from 'react';
import clsx from 'clsx';
import StringResourceManager, { GetString } from '../StringResourceManager';
import enableIcon from '../../assets/images/arrow-right.png';
import RegisteredUsersListBox from './RegisteredUsersListBox';
import { Button } from '@mui/material';
import UserDetailsForm from './UserDetailsForm';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { ApplicationState } from '../../store';
import { useEffect } from 'react';
import {
  clearRegistrationSuccessMessage,
  clearUpdateSuccessMessage,
} from '../../store/Notifications/NotificationsActions';

const useStyles = makeStyles(theme =>
  createStyles({
    gridAlign: {
      display: 'flex',
      alignItems: 'center',
    },
    margin: {
      margin: theme.spacing(1),
    },
    arrowIcon: {
      height: '50px',
      weight: '50px',
    },
    iconGrid: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: 10,
    },
    listBox: {
      border: '1px solid #000000',
    },
    box: {
      height: 60,
      display: 'flex',
      borderRadius: 5,
      padding: theme.spacing(2),
      margin: theme.spacing(1),
      backgroundColor: '#fff',
    },
    spreadBox: {
      justifyContent: 'space-around',
      alignItems: 'center',
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    send: {
      fontSize: '1.5rem',
    },
  })
);

const UserManagementForm: React.FC = (): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();
  const [selectedUser, setSelectedUser] = React.useState<string>('');
  const [userFormVisible, setUserFormVisible] = React.useState(false);
  const [disableList, setDisableList] = React.useState(false);
  const [readOnlyFields, setReadOnlyFields] = React.useState(false);
  const [hideArrowIcon, setHideArrowIcon] = React.useState(true);
  const [registrationSuccessMessage, displayRegistrationSuccessMessage] =
    React.useState(false);
  const [updateSuccessMessage, displayUpdateSuccessMessage] =
    React.useState(false);
  const [editView, setEditView] = React.useState(false);
  const registrationStatus: string = useSelector(
    (state: ApplicationState) =>
      state.notificationsReducer.registrationSuccessMessage
  );
  const updateUserDataStatus: string = useSelector(
    (state: ApplicationState) => state.notificationsReducer.updateSuccessMessage
  );
  const handleUserSelect = (username: string) => {
    setEditView(false);
    setSelectedUser(username);
    setReadOnlyFields(true);
    setHideArrowIcon(false);
    setUserFormVisible(true);
  };

  const handleAddUser = () => {
    setEditView(false);
    setSelectedUser('');
    setReadOnlyFields(false);
    setUserFormVisible(true);
    setDisableList(true);
    setHideArrowIcon(true);
  };

  const handleEditUser = (username: string) => {
    setEditView(true);
    setSelectedUser(username);
    setReadOnlyFields(false);
    setHideArrowIcon(false);
    setUserFormVisible(true);
  };

  const handleCloseUserDetailsForm = () => {
    setEditView(false);
    setUserFormVisible(false);
    setHideArrowIcon(true);
    setDisableList(false);
  };

  const handleNotificationMessage = () => {
    if (editView) {
      displayUpdateSuccessMessage(true);
    } else {
      displayRegistrationSuccessMessage(true);
    }
  };
  const resetSuccessMessage = () => {
    clearRegistrationSuccessMessage(dispatch);
  };
  const resetUpdateMessage = () => {
    clearUpdateSuccessMessage(dispatch);
  };

  useEffect(() => {
    let timeoutId: any;
    if (registrationSuccessMessage) {
      timeoutId = setTimeout(() => {
        displayRegistrationSuccessMessage(false);
        resetSuccessMessage();
      }, 3000);
    } else if (updateSuccessMessage) {
      timeoutId = setTimeout(() => {
        displayUpdateSuccessMessage(false);
        resetUpdateMessage();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [registrationSuccessMessage, updateSuccessMessage]);

  return (
    <React.Fragment>
      <Grid container spacing={2}>
        <Grid item className={clsx(classes.margin)}>
          <Grid
            container
            spacing={2}
            className={classes.gridAlign}
            style={{ width: '300px' }}
          >
            <Grid item xs={12}>
              <StringResourceManager
                variant="body1"
                style="dialogLabel"
                stringKey={'REGISTERED_USERS'}
                gutterBottom={true}
              />
            </Grid>
            <Grid item xs={12} className={classes.listBox}>
              <RegisteredUsersListBox
                onSelect={handleUserSelect}
                onClose={handleCloseUserDetailsForm}
                handleEditUser={handleEditUser}
                listAccess={disableList}
              />
            </Grid>
            <Grid item xs className={clsx(classes.spreadBox, classes.box)}>
              <Button onClick={handleAddUser} variant="contained">
                {GetString('REGISTER_USER_BUTTON')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          className={classes.iconGrid}
          style={{ visibility: hideArrowIcon ? 'hidden' : 'visible' }}
        >
          <img src={enableIcon} className={classes.arrowIcon} />
        </Grid>

        {userFormVisible && (
          <Grid item className={classes.margin}>
            <UserDetailsForm
              user={selectedUser}
              readOnlyAccess={readOnlyFields}
              closeUserDetailsForm={handleCloseUserDetailsForm}
              showRegistrationStatus={handleNotificationMessage}
              editView={editView}
            ></UserDetailsForm>
          </Grid>
        )}
      </Grid>
      <Fade>
        <NotificationGroup className={classes.bottomRight}>
          {registrationSuccessMessage && registrationStatus && (
            <Notification
              className={classes.send}
              type={{
                style: registrationStatus === 'success' ? 'success' : 'error',
                icon: true,
              }}
              closable={true}
            >
              {
                <StringResourceManager
                  stringKey={
                    registrationStatus === 'success'
                      ? 'USER_REGISTRATION_SUCCESS_MESSAGE'
                      : 'USER_REGISTRATION_FAILED_MESSAGE'
                  }
                />
              }
            </Notification>
          )}
          {updateSuccessMessage && updateUserDataStatus && (
            <Notification
              className={classes.send}
              type={{
                style: updateUserDataStatus === 'success' ? 'success' : 'error',
                icon: true,
              }}
              closable={true}
            >
              {
                <StringResourceManager
                  stringKey={
                    updateUserDataStatus === 'success'
                      ? 'UPDATE_SUCCESS_MESSAGE'
                      : 'UPDATE_FAILED_MESSAGE'
                  }
                />
              }
            </Notification>
          )}
        </NotificationGroup>
      </Fade>
    </React.Fragment>
  );
};

export default UserManagementForm;
